import { UseStateReturn } from "@/fixtures/globalConstants";
import { CUSTOM_SCHEDULE_ITEM_KEY } from "@/pages/Posts/PostCard/ScheduleCustomTime/useScheduleCustomTime";
import SocialsSelect from "@/pages/Posts/PostCard/SocialsSelect";
import { DownOutlined } from "@ant-design/icons";
import { ButtonProps } from "@tremor/react";
import { Button, Dropdown, Tooltip } from "antd";
import { DropdownButtonProps } from "antd/es/dropdown/dropdown-button";
import useUserPermissions from "config/UserPermissionsContext/useUserPermissions";
import { useAppSelector } from "store";
import { Post } from "store/posts/postConstants";
import { IntegrationType } from "store/user/userConstants";
import { integrationsTypeGetter } from "store/user/userSlice";
import {
  DropdownButtonConfig,
  SaveDraftActionKey,
  ScheduleActionKey,
} from "../postEditorConstants";

type PostEditorFooterProps = {
  post?: Post;
  mediaUrls: string[];
  loading: boolean | string;
  disabled?: boolean;
  dropdownButtons: DropdownButtonConfig[];
  handleAction: (
    action:
      | SaveDraftActionKey
      | ScheduleActionKey
      | typeof CUSTOM_SCHEDULE_ITEM_KEY
  ) => void;
  publishToState: UseStateReturn<IntegrationType[] | null>;
  close: () => void;
};

const PostEditorFooter = ({
  post,
  mediaUrls,
  loading,
  disabled,
  dropdownButtons,
  handleAction,
  publishToState,
  close,
}: PostEditorFooterProps) => {
  const { hasPermission, hasSomePermissions } = useUserPermissions();
  const connectedSocials = useAppSelector(integrationsTypeGetter);

  if (
    !hasSomePermissions(["post_status_draft_write", "posting_schedule_write"])
  ) {
    return (
      <Button type="primary" onClick={close} disabled={disabled}>
        Close
      </Button>
    );
  }

  return (
    <div className="w-full flex max-sm:flex-col gap-2 justify-between items-center">
      {(post || connectedSocials.length > 0) && (
        <div className="flex items-center gap-2 pl-2 min-w-max">
          Publish to <SocialsSelect publishToState={publishToState} />
        </div>
      )}
      <div className="flex max-sm:flex-col-reverse max-sm:w-full gap-2 [&_*]:w-[unset] ml-auto">
        <Button onClick={close} type="text" disabled={disabled}>
          Cancel
        </Button>
        {dropdownButtons.map(
          (
            {
              labelIcon,
              permissionNeeded,
              items,
              menuActionsRequirePost,
              labelActionKey,
              label,
              loadingKeys,
              buttonType,
            },
            index
          ) => {
            if (!hasPermission(permissionNeeded)) {
              return null;
            }

            const showDropdownItems = post || !menuActionsRequirePost;
            const isEffectivelyDisabled =
              disabled || Boolean(loading) || (!post && !mediaUrls.length);
            const showMediaTooltip =
              !disabled && !loading && !post && !mediaUrls.length;
            const tooltipTitle = "Please add media to save or schedule.";

            const commonProps: Pick<
              DropdownButtonProps & ButtonProps,
              "loading" | "disabled" | "onClick" | "children"
            > = {
              loading: loadingKeys.includes(loading as string),
              disabled: isEffectivelyDisabled,
              onClick: () => handleAction(labelActionKey),
              children: (
                <div className="flex gap-2 items-center">
                  {labelIcon}
                  {label}
                </div>
              ),
            };

            const ButtonComponent = showDropdownItems ? (
              <Dropdown.Button
                {...commonProps}
                key={index}
                type={buttonType}
                icon={<DownOutlined />}
                className="[&_button]:flex-grow"
                menu={{
                  items,
                  onClick: ({ key }) =>
                    handleAction(key as SaveDraftActionKey | ScheduleActionKey),
                }}
              />
            ) : (
              <Button key={index} type={buttonType} {...commonProps} />
            );

            return (
              hasPermission(permissionNeeded) && (
                <Tooltip
                  key={index}
                  title={showMediaTooltip ? tooltipTitle : undefined}
                >
                  {isEffectivelyDisabled ? (
                    <span
                      style={{ display: "inline-block", cursor: "not-allowed" }}
                    >
                      {ButtonComponent}
                    </span>
                  ) : (
                    ButtonComponent
                  )}
                </Tooltip>
              )
            );
          }
        )}
      </div>
    </div>
  );
};

export default PostEditorFooter;
